import React, { useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  SelectChangeEvent
} from '@mui/material';
import { HomeExpenseGroupCategoryDto } from '../types/homeExpenses';
import { useCategories } from '../hooks/useCategories';

interface CategorySelectorProps {
  value?: string; // Category publicId
  onChange: (categoryId?: string) => void;
  disabled?: boolean;
  required?: boolean;
  label?: string;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  label = "Categoria"
}) => {
  const { categories, loading, fetchCategories } = useCategories();

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    onChange(selectedValue === '' ? undefined : selectedValue);
  };

  const renderValue = (selected: string) => {
    if (!selected) return '';
    
    const category = categories.find(cat => cat.publicId === selected);
    if (!category) return '';

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: category.color || '#ccc',
          }}
        />
        <Typography>{category.name}</Typography>
      </Box>
    );
  };

  return (
    <FormControl fullWidth disabled={disabled || loading}>
      <InputLabel>{label}{required && ' *'}</InputLabel>
      <Select
        value={value || ''}
        onChange={handleChange}
        label={label + (required ? ' *' : '')}
        renderValue={renderValue}
      >
        <MenuItem value="">
          <em>Sem categoria</em>
        </MenuItem>
        {categories
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
          .map((category) => (
            <MenuItem key={category.publicId} value={category.publicId}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: category.color || '#ccc',
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {category.name}
                  </Typography>
                  {category.description && (
                    <Typography variant="caption" color="text.secondary">
                      {category.description}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  label={category.sortOrder}
                  sx={{ 
                    fontSize: '0.7rem',
                    height: 20,
                    color: 'text.secondary' 
                  }}
                />
              </Box>
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};

export default CategorySelector;
